import { useEffect, useState } from "react";
import { Public, getAuth } from "@/lib/api";
import { navigate } from "@/lib/router";
import { Sparkles, X } from "lucide-react";

const STORAGE_KEY = "ztb_welcome_gift_dismissed_at";
const SUPPRESS_HOURS = 24;

type Gift = {
  enabled: boolean;
  vip_days: number;
  points: number;
  hero_title: string;
  hero_message: string;
  cta_label: string;
};

/** 匿名访客赠礼弹窗：仅当未登录、站点启用 welcome_gift、且 24 小时内未关闭时显示。 */
export function WelcomeGiftDialog() {
  const [gift, setGift] = useState<Gift | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (getAuth()) return;
    // 24 小时内已关闭
    try {
      const ts = Number(localStorage.getItem(STORAGE_KEY) || "0");
      if (ts && Date.now() - ts < SUPPRESS_HOURS * 3600 * 1000) return;
    } catch {}
    Public.welcomeGift().then((g) => {
      if (g.enabled && (g.vip_days > 0 || g.points > 0)) {
        setGift(g);
        // 让页面先渲染，再淡入
        setTimeout(() => setOpen(true), 600);
      }
    }).catch(() => {});
  }, []);

  if (!gift || !open) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    setOpen(false);
  };
  const claim = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    setOpen(false);
    navigate("/register", { welcome: "1" });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
         role="dialog" aria-modal="true" onClick={dismiss}>
      <div onClick={(e) => e.stopPropagation()}
           className="relative w-full max-w-md overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-rose-50 to-violet-100 shadow-2xl ring-1 ring-black/5">
        <button onClick={dismiss} aria-label="关闭"
                className="absolute right-3 top-3 rounded-full p-1.5 text-slate-500 hover:bg-black/5 hover:text-slate-800">
          <X className="h-4 w-4" />
        </button>
        <div className="px-6 pt-8 pb-6 text-center">
          <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-rose-500 text-white shadow-lg">
            <Sparkles className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">{gift.hero_title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 whitespace-pre-line">
            {gift.hero_message}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            {gift.vip_days > 0 && (
              <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-rose-700 ring-1 ring-rose-200">
                {gift.vip_days} 天 VIP
              </span>
            )}
            {gift.points > 0 && (
              <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
                +{gift.points} 积分
              </span>
            )}
          </div>
          <div className="mt-6 grid gap-2">
            <button onClick={claim}
                    className="w-full rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white shadow hover:bg-slate-800 transition">
              {gift.cta_label}
            </button>
            <button onClick={dismiss}
                    className="w-full rounded-lg py-2 text-xs text-slate-500 hover:text-slate-700">
              下次再说
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
